<template>
  <div class="h-full">
    <!-- Main Content -->
    <div class="w-full h-full">
      <AccountInfo class="" />
      <div class="relative">
        <!-- Persistent Loading Background -->
        <div class="absolute inset-0 z-0" v-if="shouldShowLoading">
          <TextBlob headingSize="s">
            <template #heading><span class="opacity-0">Loading</span></template>
            <template #text>
              <div class="flex justify-center items-center min-h-[200px]">
                <LoadingIndicator :loading="true" />
              </div>
            </template>
          </TextBlob>
        </div>

        <!-- Tab Content -->
        <div class="relative z-10">
          <Transition name="fade" mode="out-in">
            <div :key="activeTab">
              <component :is="currentComponent" />
            </div>
          </Transition>
        </div>
      </div>
    </div>

    <!-- Sidebar -->
    <div class="absolute top-0 left-0 z-20">
      <SidebarNav v-model:collapsed="navCollapsed" />
      <!-- Toggle Button -->
      <button
        ref="toggleButton"
        data-menu-toggle
        @click="navCollapsed = !navCollapsed"
        class="absolute top-[88px] bg-gray-710 rounded-full p-1.5 hover:bg-gray-600 transition-all duration-300 z-10 shadow-md"
        :style="{ left: navCollapsed ? '0px' : '140px' }"
      >
        <span class="text-white text-lg">
          {{ navCollapsed ? ">" : "<" }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup>
import SidebarNav from "@/views/explorer/components/SidebarNav.vue";
import AccountInfo from "@/views/explorer/components/AccountInfo.vue";
import ExplorerDashboard from "@/views/explorer/ExplorerDashboard.vue";
import ExplorerLogin from "@/views/explorer/ExplorerLogin.vue";
import ExplorerReviews from "@/views/explorer/ExplorerReviews.vue";
import ExplorerAbout from "@/views/explorer/ExplorerAbout.vue";
import ExplorerFAQ from "@/views/explorer/ExplorerFAQ.vue";
import ExplorerContact from "@/views/explorer/ExplorerContact.vue";
import ExplorerSettings from "@/views/explorer/ExplorerSettings.vue";
import ExplorerReviewers from "./explorer/ExplorerReviewers.vue";
import ExplorerChangelog from "./explorer/ExplorerChangelog.vue";
import TextBlob from "@/components/TextBlob.vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";

import { computed, watch, ref, markRaw } from "vue";
import { useUser } from "@/stores";

const store = useUser();
const activeTab = computed(() => store.activeTab);
const user = computed(() => store.user);
const navCollapsed = ref(false);

// Tabs that should show loading state
const loadingEnabledTabs = [
  "/explorer/reviews",
  "/explorer/reviewers",
  "/explorer/settings",
  "/explorer/about",
  "/explorer/faq",
  "/explorer/contact",
  "/explorer/changelog",
];

const shouldShowLoading = computed(() =>
  loadingEnabledTabs.includes(activeTab.value)
);

const currentComponent = computed(() => {
  switch (activeTab.value) {
    case "/explorer/dashboard":
      return markRaw(ExplorerDashboard);
    case "/explorer/reviews":
      return user.value ? markRaw(ExplorerReviews) : null;
    case "/explorer/about":
      return markRaw(ExplorerAbout);
    case "/explorer/faq":
      return markRaw(ExplorerFAQ);
    case "/explorer/contact":
      return markRaw(ExplorerContact);
    case "/explorer/settings":
      return markRaw(ExplorerSettings);
    case "/explorer/reviewers":
      return markRaw(ExplorerReviewers);
    case "/explorer/changelog":
      return markRaw(ExplorerChangelog);
    default:
      return null;
  }
});

watch(activeTab, () => {
  window.scrollTo(0, 0);
});
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
